import {createTheme, alpha} from '@mui/material/styles';
const defaultTheme = createTheme();

export const brand = {
    50: 'hsl(213, 12%, 95%)',
    100: 'hsl(213, 11%, 88%)',
    200: 'hsl(214, 9%, 70%)',
    300: 'hsl(214, 8%, 45%)',
    400: 'hsl(215, 12%, 18%)',
    500: 'hsl(215, 13%, 14%)',
    600: 'hsl(214, 10%, 28%)',
    700: 'hsl(216, 12%, 12%)',
    800: 'hsl(216, 14%, 8%)',
    900: 'hsl(216, 16%, 5%)'
};

export const blue = {
    50: 'hsl(210, 100%, 95%)',
    100: 'hsl(210, 100%, 92%)',
    200: 'hsl(210, 100%, 80%)',
    300: 'hsl(210, 100%, 65%)',
    400: 'hsl(210, 98%, 48%)',
    500: 'hsl(210, 98%, 42%)',
    600: 'hsl(210, 98%, 55%)',
    700: 'hsl(210, 100%, 35%)',
    800: 'hsl(210, 100%, 16%)',
    900: 'hsl(210, 100%, 21%)'
};

export const gray = {
    50: 'hsl(210, 20%, 98%)',
    100: 'hsl(210, 16%, 95%)',
    200: 'hsl(214, 15%, 89%)',
    300: 'hsl(213, 12%, 81%)',
    400: 'hsl(213, 10%, 65%)',
    500: 'hsl(213, 9%, 42%)',
    600: 'hsl(214, 12%, 33%)',
    700: 'hsl(215, 15%, 23%)',
    800: 'hsl(215, 20%, 10%)',
    900: 'hsl(216, 24%, 5%)'
};

export const green = {
    50: 'hsl(120, 80%, 98%)',
    100: 'hsl(120, 75%, 94%)',
    200: 'hsl(120, 75%, 87%)',
    300: 'hsl(120, 61%, 77%)',
    400: 'hsl(120, 44%, 53%)',
    500: 'hsl(120, 59%, 30%)',
    600: 'hsl(120, 70%, 25%)',
    700: 'hsl(120, 75%, 16%)',
    800: 'hsl(120, 84%, 10%)',
    900: 'hsl(120, 87%, 6%)'
};

export const amber = {
    50: '#fff8e1',
    100: '#ffecb4',
    200: '#ffdf83',
    300: '#ffd451',
    400: '#ffc92c',
    500: '#ffc013',
    600: '#ffb20d',
    700: '#fe9f0c',
    800: '#fe8f0c',
    900: '#fe6f0a'
};

// The old name for `amber` — kept so existing imports keep working. Not orange;
// warning orange is `warningOrange`.
export const orange = amber;

export const yellow = {
    50: 'hsl(45, 100%, 97%)',
    100: 'hsl(45, 92%, 90%)',
    200: 'hsl(45, 94%, 80%)',
    300: 'hsl(45, 90%, 65%)',
    400: 'hsl(45, 90%, 40%)',
    500: 'hsl(45, 90%, 35%)',
    600: 'hsl(45, 91%, 25%)',
    700: 'hsl(45, 94%, 20%)',
    800: 'hsl(45, 95%, 16%)',
    900: 'hsl(45, 93%, 12%)'
};

export const red = {
    50: 'hsl(0, 100%, 97%)',
    100: 'hsl(0, 92%, 90%)',
    200: 'hsl(0, 94%, 80%)',
    300: 'hsl(0, 90%, 65%)',
    400: 'hsl(0, 90%, 40%)',
    500: 'hsl(0, 90%, 30%)',
    600: 'hsl(0, 91%, 25%)',
    700: 'hsl(0, 94%, 18%)',
    800: 'hsl(0, 95%, 12%)',
    900: 'hsl(0, 93%, 6%)'
};

// Warning is ORANGE, and every component draws it from this one set (#2303).
// It used to be defined five ways — a dark gold `warning.main` used as text,
// pale-amber chip and button fills, a cream alert, a too-light `text.warning`
// — so a warning bar never matched the warning chip beside it. Roles:
//   surface  tints behind content (standard alerts, cards, grid rows)
//   tint     a stronger tint, for hover and selected states on a surface
//   border   the edge of a tinted surface
//   fill     anything solid: bars, chips, contained buttons, badges, filled alerts
//   onFill   text and icons on the fill (dark: white on orange is 2.2:1)
//   text     warning-coloured text on white (#a85200, 5.4:1 — orange itself
//            is too light to read as text)
//   icon     a warning icon on a light surface
// There is deliberately no separate OUTLINED warning look: an outlined warning
// chip, button or alert renders like the filled/standard one.
//
// `amber` below is the yellow scale the old warning came from (exported as
// `orange` for history — it never was orange). It still serves the yellow
// "needed" stage and other non-warning accents.
export const warningOrange = {
    surface: '#fff3e0',
    tint: '#ffe0b2',
    border: '#ffcc80',
    fill: '#ff9800',
    fillHover: '#fb8c00',
    onFill: '#3d2400',
    text: '#a85200',
    icon: '#f57c00'
};

// The success FILL is a darker green than green[400] so a green bar or chip
// stays distinguishable from the orange beside it for a red-green colour-blind
// reader (OKLab ΔE 11.5 against warningOrange.fill; green[400] was 3.3).
export const successGreen = {
    fill: '#3a8f3e',
    fillHover: '#2f7a33'
};

export const colorSchemes = {
    light: {
        palette: {
            primary: {
                light: brand[200],
                main: brand[400],
                dark: brand[700],
                contrastText: brand[50]
            },
            info: {
                light: blue[100],
                main: blue[400],
                dark: blue[700],
                contrastText: gray[50]
            },
            // See warningOrange: `main` is the fill, `dark` the text colour.
            warning: {
                light: warningOrange.surface,
                main: warningOrange.fill,
                dark: warningOrange.text,
                contrastText: warningOrange.onFill
            },
            error: {
                light: red[300],
                main: red[400],
                dark: red[800]
            },
            success: {
                light: green[300],
                main: successGreen.fill,
                dark: green[800],
                contrastText: '#fff'
            },
            grey: {
                ...gray
            },
            divider: 'hsl(214, 15%, 90%)',
            background: {
                default: 'hsl(210, 20%, 98.9%)',
                paper: 'hsl(0, 0%, 100%)'
            },
            text: {
                primary: gray[800],
                secondary: gray[600],
                warning: warningOrange.text
            },
            action: {
                hover: alpha(gray[200], 0.2),
                selected: `${alpha(gray[200], 0.3)}`
            },
            baseShadow: 'hsla(215, 30%, 8%, 0.07) 0px 4px 16px 0px, hsla(215, 25%, 12%, 0.08) 0px 8px 16px -5px',
            // Stages
            complete: {
                main: green[400],
                contrastText: '#fff'
            },
            needed: {
                main: orange[300],
                contrastText: '#fff'
            },
            inProgress: {
                main: warningOrange.fill,
                contrastText: '#fff'
            },
            // Dark slate "selected" fill for the deployment rotation/apparatus
            // selectors (design handoff 8u).
            selectedSurface: '#282d34'
        }
    }
};

export const typography = {
    fontFamily: ['"Inter", "sans-serif"'].join(','),
    h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: -0.5
    },
    h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 600,
        lineHeight: 1.2
    },
    h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        lineHeight: 1.2
    },
    h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.5
    },
    h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
        lineHeight: 1.6
    },
    h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
        lineHeight: 1.8
    },
    subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18)
    },
    subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500
    },
    body1: {
        fontSize: defaultTheme.typography.pxToRem(14)
    },
    body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400
    },
    caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400
    },
    overline: {
        fontSize: defaultTheme.typography.pxToRem(11),
        fontWeight: 600,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        lineHeight: 1.6,
        opacity: 0.9
    }
};

export const shape = {
    borderRadius: 8
};

// @ts-ignore
const defaultShadows = [
    'none',
    'var(--template-palette-baseShadow)',
    ...defaultTheme.shadows.slice(2)
];

export const shadows = defaultShadows;
